import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown, Form } from "react-bootstrap";
import MultiselectValue from "./MultiselectValue";
import { MdClose } from "react-icons/md";

// The forwardRef is important!!
// Dropdown needs access to the DOM node in order to position the Menu
const CustomToggle = React.forwardRef(({ 
    inputRef,
    searchTerm, setSearchTerm, addItem, value, onChange, options, placeholder, 
    setShowDropdown, onClick, toggleDropdown, setToggleDropdown, focused, setFocused, 
    isDropdownShowing
}, ref) => {
    const onAdd = useCallback(() => {
        const found = options.find((o) => o.label === focused);
        if(found) {
            addItem(found);
        }
    }, [focused, options, addItem]);

    useEffect(() => {
        setToggleDropdown(() => {
            return () => onClick({});
        });
    }, [setToggleDropdown, onClick]);

    return (
        <Form.Group 
            ref={ref}
            className="mb-3"
        >
            <div className="d-flex align-items-center multi-value-search-dropdown-container">
                <div className="d-flex multi-value-search-dropdown"
                    onClick={() => { 
                        inputRef.current.focus();
                        if(isDropdownShowing()) {
                            // hiding, clear text box
                            setSearchTerm("");
                        }
                        else { // showing, set first to be focused
                            if(options.length > 0) {
                                setFocused(options[0].label);
                            }
                        }
                        toggleDropdown();
                    }}>
                    {value.map((item) => (
                        <MultiselectValue key={item.label} value={item.label} onDeselect={() => onChange((items) => items.filter((it) => it.label !== item.label))} />
                    ))}
                    <div style={{
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: "auto",
                    }}>
                        <Form.Control
                            type="text"
                            ref={inputRef}
                            value={searchTerm}
                            onBlur={() => setSearchTerm("")} // lost focus
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={(e) => {
                                if(e.key === "Enter" || e.key === "Tab") {
                                    if(isDropdownShowing()) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        onAdd();
                                    }
                                }
                                else if(e.key === "Backspace" && searchTerm.length === 0 && value.length > 0) {
                                    onChange(value.slice(0, value.length-1));
                                }
                                else if(e.key === "ArrowDown" || e.key === "ArrowUp") { // prevent dropdown from stealing focus
                                    e.stopPropagation();
                                    if(options.length > 0) {
                                        setFocused((focused) => {
                                            const currentFocusIndex = options.findIndex((o) => o.label === focused);
                                            const diff = e.key === "ArrowDown" ? 1 : -1;
                                            const newFocusIndex = ((currentFocusIndex < 0 ? 0 : currentFocusIndex) + diff + options.length) % options.length;
                                            const newFocusLabel = options[newFocusIndex].label;
                                            return newFocusLabel;
                                        });
                                    }
                                }
                                else if(e.key.length === 1) {
                                    setShowDropdown(true);
                                }
                            }}
                            placeholder={value.length > 0 ? "" : placeholder}
                            className="disguised-input"
                            data-testid="multi-value-input"
                        />
                    </div>
                </div>
                {(value.length > 0) && <MdClose
                    className="clear-tag"
                    style={{
                        margin: "0px 8px"
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        onChange([]);
                    }}
                >X</MdClose>}
            </div>
        </Form.Group>
    )
});
CustomToggle.displayName = "CustomToggle";

const MultiValueSearchDropdown = ({ 
    value, onChange,
    options,
    placeholder="Select item..."
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [toggleDropdown, setToggleDropdown] = useState(false);
    const [dropdownMenu, setdropdownMenu] = useState(null);
    const [focused, setFocusedRaw] = useState(null);
    const inputRef = useRef(null);

    const setFocused = useCallback((newFocus) => {
        setFocusedRaw((focused) => {
            let newLabel = typeof(newFocus) === "function" ? newFocus(focused) : newFocus;
            const element = document.getElementById(`dropdown-mvsd-${newLabel.replace(/\s+/g, '-')}`);
            if(element) {
                if(element.scrollIntoViewIfNeeded)
                    element.scrollIntoViewIfNeeded(false);
                else element.scrollIntoView(); // scrollIntoViewIfNeeded is not supported by Firefox
            }
            return newLabel;
        });
    }, [setFocusedRaw]);

    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
    
    // Debounce search term updates
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 100); // 300ms delay

        return () => clearTimeout(timer);
    }, [searchTerm]);

    // Memoize the filtered items
    const filteredOptions = useMemo(() => {
        if(!options) return [];

        const validOptions = options.filter((o) => !value.some(((v) => v.label === o.label)));

        if (!debouncedSearchTerm.length) {
            return validOptions;
        }

        const searchTermLower = debouncedSearchTerm.toLowerCase();
        return validOptions.filter((o) => o.label.toLowerCase().includes(searchTermLower));
    }, [debouncedSearchTerm, options, value]);

    useEffect(() => {
        if(filteredOptions.length > 0) {
            setFocused((focused) => {
                if(filteredOptions.some((o) => o.label === focused)) { // still has it
                    return focused;
                }
                return filteredOptions[0].label;
            });
        }
    }, [filteredOptions, setFocused]);

    const isDropdownShowing = useCallback(() => {
        return dropdownMenu && dropdownMenu.className.includes("show");
    }, [dropdownMenu]);

    const setShowDropdown = useCallback((show) => {
        if(show !== isDropdownShowing()) {
            toggleDropdown();
        }
    }, [toggleDropdown, isDropdownShowing]);

    const addItem = useCallback((item) => {
        onChange((items) => [...items, item]);
        setSearchTerm("");
        setShowDropdown(false);
        inputRef.current.focus();
    }, [onChange, setShowDropdown]);

    return <>
            <Dropdown
                data-bs-theme="dark"
                variant="dark"
                id="bg-nested-dropdown"
                className="dark-dropdown-focus"
            >
                <Dropdown.Toggle 
                    as={CustomToggle}
                    inputRef={inputRef}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    addItem={addItem}
                    value={value}
                    onChange={onChange}
                    options={filteredOptions}
                    placeholder={placeholder}
                    setShowDropdown={setShowDropdown}
                    toggleDropdown={toggleDropdown}
                    setToggleDropdown={setToggleDropdown}
                    focused={focused}
                    setFocused={setFocused}
                    isDropdownShowing={isDropdownShowing}
                />
                <Dropdown.Menu ref={(newRef) => setdropdownMenu(newRef)}>
                    <div style={{
                        maxHeight: "30vh",
                        overflowY: "scroll"
                    }}>
                        {(filteredOptions.length === 0) && <Dropdown.Header>No options</Dropdown.Header>}
                        {filteredOptions.map((option) => (
                            <Dropdown.Item
                                eventKey={option.label}
                                onClick={() => addItem(option)}
                                key={`dropdown-mvsd-${option.label.replace(/\s+/g, '-')}`}
                                id={`dropdown-mvsd-${option.label.replace(/\s+/g, '-')}`}
                                style={{outline: "none"}}
                                active={option.label === focused}
                                onMouseEnter={() => setFocused(option.label)}
                            >
                                {option.label}
                            </Dropdown.Item>
                        ))}
                    </div>
                </Dropdown.Menu>
            </Dropdown>
    </>;
};

export default MultiValueSearchDropdown;